import { DataMessageType, model as sse, type DataEvent } from '!/sse'
import { combine, createEvent, createStore, sample, split } from 'effector'
import { model as session } from '~/entities/session'
import { aye, nay } from '~/shared/helpers'
import {
  getShowBundleInfoFx,
  getTvBundleInfoFx,
  getTvChannelsByIdFx,
  getTvShowBaseFx,
  getTvShowEpisodeFx,
  getTvShowEpisodeWithoutSeasonFx,
  getTvShowSeasonByIdBaseFx,
  getVodBundleInfoFx,
  getVodFx,
} from './api'
import { type Purchased, type SseLoadedInfo } from './index.h'
import { adjust, updateMap } from './lib'

const addPurchase = createEvent<DataEvent['data'] & SseLoadedInfo>()

const $purchased = createStore<Purchased>({
  channel: new Map(),
  channelSet: new Map(),
  episode: new Map(),
  liveEvent: new Map(),
  partnerProduct: new Map(),
  season: new Map(),
  subscription: new Map(),
  tvShow: new Map(),
  tvShowSet: new Map(),
  vod: new Map(),
  vodSet: new Map(),
})
  .reset(session.$session)
  .on(addPurchase, (purchased, { type, itemId, purchaseInfo }) => {
    const push = updateMap(purchased)

    switch (type) {
      case DataMessageType.LIVE_EVENT_PURCHASE_EVENT:
        return push('liveEvent', itemId)
      case DataMessageType.PARTNER_PRODUCT_PURCHASE_EVENT:
        return push('partnerProduct', itemId)
      case DataMessageType.SUBSCRIPTION_PURCHASE_EVENT:
        return push('subscription', itemId)
      case DataMessageType.CHANNEL_PURCHASE_EVENT:
        return push('channel', itemId, purchaseInfo)
      case DataMessageType.CHANNEL_SET_PURCHASE_EVENT:
        return push('channelSet', itemId, purchaseInfo)
      case DataMessageType.EPISODE_PURCHASE_EVENT:
        return push('episode', itemId, purchaseInfo)
      case DataMessageType.SEASON_PURCHASE_EVENT:
        return push('season', itemId, purchaseInfo)
      case DataMessageType.TV_SHOW_PURCHASE_EVENT:
        return push('tvShow', itemId, purchaseInfo)
      case DataMessageType.TV_SHOW_SET_PURCHASE_EVENT:
        return push('tvShowSet', itemId, purchaseInfo)
      case DataMessageType.VOD_PURCHASE_EVENT:
        return push('vod', itemId, purchaseInfo)
      case DataMessageType.VOD_SET_PURCHASE_EVENT:
        return push('vodSet', itemId, purchaseInfo)
      default:
        return purchased
    }
  })

const {
  vodSetPurchase,
  vodPurchase,
  channelPurchase,
  channelSetPurchase,
  episodePurchase,
  seasonPurchase,
  tvShowPurchase,
  tvShowSetPurchase,
  liveEventPurchase,
  partnerProductPurchase,
  subscriptionPurchase,
} = split(sse.dataMessage, {
  vodSetPurchase: (data) =>
    data.type === DataMessageType.VOD_SET_PURCHASE_EVENT,
  vodPurchase: (data) => data.type === DataMessageType.VOD_PURCHASE_EVENT,
  channelPurchase: (data) =>
    data.type === DataMessageType.CHANNEL_PURCHASE_EVENT,
  channelSetPurchase: (data) =>
    data.type === DataMessageType.CHANNEL_SET_PURCHASE_EVENT,
  episodePurchase: (data) =>
    data.type === DataMessageType.EPISODE_PURCHASE_EVENT,
  seasonPurchase: (data) => data.type === DataMessageType.SEASON_PURCHASE_EVENT,
  tvShowPurchase: (data) =>
    data.type === DataMessageType.TV_SHOW_PURCHASE_EVENT,
  tvShowSetPurchase: (data) =>
    data.type === DataMessageType.TV_SHOW_SET_PURCHASE_EVENT,
  liveEventPurchase: (data) =>
    data.type === DataMessageType.LIVE_EVENT_PURCHASE_EVENT,
  partnerProductPurchase: (data) =>
    data.type === DataMessageType.PARTNER_PRODUCT_PURCHASE_EVENT,
  subscriptionPurchase: (data) =>
    data.type === DataMessageType.SUBSCRIPTION_PURCHASE_EVENT,
})

sample({
  clock: [liveEventPurchase, partnerProductPurchase, subscriptionPurchase],
  target: addPurchase,
})

// VOD SET PURCHASE
sample({
  clock: vodSetPurchase,
  fn: (data) => ({ bundleId: data.itemId }),
  target: getVodBundleInfoFx,
})
sample({
  clock: getVodBundleInfoFx.doneData,
  fn: (data) => ({
    type: DataMessageType.VOD_SET_PURCHASE_EVENT,
    itemId: data.id,
    purchaseInfo: data.purchaseInfo,
  }),
  target: addPurchase,
})

// VOD PURCHASE
sample({
  clock: vodPurchase,
  fn: ({ itemId }) => ({ vodId: itemId }),
  target: getVodFx,
})
sample({
  clock: getVodFx.doneData,
  fn: (data) => {
    return {
      type: DataMessageType.VOD_PURCHASE_EVENT,
      itemId: Number(data.id),
      purchaseInfo: data.purchaseInfo,
    }
  },
  target: addPurchase,
})

// CHANNEL SET PURCHASE
sample({
  clock: channelSetPurchase,
  fn: (data) => ({ bundleId: data.itemId }),
  target: getTvBundleInfoFx,
})
sample({
  clock: getTvBundleInfoFx.doneData,
  fn: (data) => ({
    type: DataMessageType.CHANNEL_SET_PURCHASE_EVENT,
    itemId: data.id,
    purchaseInfo: data.purchaseInfo,
  }),
  target: addPurchase,
})

// CHANNEL PURCHASE
sample({
  clock: channelPurchase,
  fn: ({ itemId }) => ({ ids: [itemId] }),
  target: getTvChannelsByIdFx,
})
sample({
  clock: getTvChannelsByIdFx.doneData,
  filter: (data) => data.length > 0,
  fn: (data) => {
    const channel = data[0]
    return {
      type: DataMessageType.CHANNEL_PURCHASE_EVENT,
      itemId: Number(channel.id),
      purchaseInfo: channel.purchaseInfo,
    }
  },
  target: addPurchase,
})

// TV SHOW SET PURCHASE
sample({
  clock: tvShowSetPurchase,
  fn: (data) => ({ bundleId: data.itemId }),
  target: getShowBundleInfoFx,
})
sample({
  clock: getShowBundleInfoFx.doneData,
  fn: (data) => ({
    type: DataMessageType.TV_SHOW_SET_PURCHASE_EVENT,
    itemId: data.id,
    purchaseInfo: data.purchaseInfo,
  }),
  target: addPurchase,
})

// TV SHOW PURCHASE
sample({
  clock: tvShowPurchase,
  fn: ({ itemId }) => ({ tvShowId: itemId }),
  target: getTvShowBaseFx,
})
sample({
  clock: getTvShowBaseFx.doneData,
  filter: (data) => data.length > 0,
  fn: (data) => {
    const show = data[0]
    return {
      type: DataMessageType.TV_SHOW_PURCHASE_EVENT,
      itemId: Number(show.id),
      purchaseInfo: show.purchaseInfo,
    }
  },
  target: addPurchase,
})

// SEASON PURCHASE
sample({
  clock: seasonPurchase,
  filter: ({ metadata }) => aye(metadata) && aye(metadata.tvShowId),
  fn: ({ itemId, metadata }) => ({
    seasonId: itemId,
    tvShowId: metadata!.tvShowId!,
  }),
  target: getTvShowSeasonByIdBaseFx,
})
sample({
  clock: getTvShowSeasonByIdBaseFx.doneData,
  fn: (data) => {
    return {
      type: DataMessageType.SEASON_PURCHASE_EVENT,
      itemId: Number(data.id),
      purchaseInfo: data.purchaseInfo,
    }
  },
  target: addPurchase,
})

// EPISODE PURCHASE
// if season is not provided, get episode without season
sample({
  clock: episodePurchase,
  filter: (filterData) => {
    return (
      aye(filterData.metadata) &&
      aye(filterData.metadata.tvShowId) &&
      nay(filterData.metadata.seasonId)
    )
  },
  fn: ({ itemId, metadata }) => ({
    episodeId: itemId,
    tvShowId: metadata!.tvShowId!,
  }),
  target: getTvShowEpisodeWithoutSeasonFx,
})

// if season is provided, get episode with season
sample({
  clock: episodePurchase,
  filter: ({ metadata }) =>
    aye(metadata) && aye(metadata.tvShowId) && aye(metadata.seasonId),
  fn: ({ itemId, metadata }) => ({
    episodeId: itemId,
    tvShowId: metadata!.tvShowId!,
    seasonId: metadata!.seasonId!,
  }),
  target: getTvShowEpisodeFx,
})

sample({
  clock: [
    getTvShowEpisodeFx.doneData,
    getTvShowEpisodeWithoutSeasonFx.doneData,
  ],
  fn: (data) => ({
    type: DataMessageType.EPISODE_PURCHASE_EVENT,
    itemId: Number(data.id),
    purchaseInfo: data.purchaseInfo,
  }),
  target: addPurchase,
})

export const $channelIds = $purchased.map((p) => p.channel)
export const $channelSetIds = $purchased.map((p) => p.channelSet)
export const $episodeIds = $purchased.map((p) => p.episode)
export const $liveEventIds = $purchased.map((p) => p.liveEvent)
export const $partnerProductIds = $purchased.map((p) => p.partnerProduct)
export const $seasonIds = $purchased.map((p) => p.season)
export const $subscriptionIds = $purchased.map((p) => p.subscription)
export const $tvShowIds = $purchased.map((p) => p.tvShow)
export const $tvShowSetIds = $purchased.map((p) => p.tvShowSet)
export const $vodIds = $purchased.map((p) => p.vod)
export const $vodSetIds = $purchased.map((p) => p.vodSet)

const $s = session.$session
export const $channel = combine($purchased, $s, adjust('channel', 'channelSet'))
export const $channelSet = combine(
  $purchased,
  $s,
  adjust('channelSet', 'channelSet')
)
export const $episode = combine($purchased, $s, adjust('episode', 'tvShowSet'))
export const $liveEvent = combine($purchased, $s, adjust('liveEvent'))
export const $partnerProduct = combine($purchased, $s, adjust('partnerProduct'))
export const $season = combine($purchased, $s, adjust('season', 'tvShowSet'))
export const $subscription = combine($purchased, $s, adjust('subscription'))
export const $tvShow = combine($purchased, $s, adjust('tvShow', 'tvShowSet'))
export const $tvShowSet = combine(
  $purchased,
  $s,
  adjust('tvShowSet', 'tvShowSet')
)
export const $vod = combine($purchased, $s, adjust('vod', 'vodSet'))
export const $vodSet = combine($purchased, $s, adjust('vodSet', 'vodSet'))

// generic adjust for any content type
export const $adjust = combine(
  $purchased,
  $s,
  (purchased, session) => (type: keyof Purchased, bundle?: keyof Purchased) =>
    adjust(type, bundle)(purchased, session)
)
